import type { LampSimilarityDetail } from '../types/project.ts';
import type { Version } from '../types/project.ts';

/**
 * True when at least one luminaire in the version has a catalogue product/variant
 * assigned. Used for identity checks: show the product-picker UI, assignment badges,
 * "Assign product" buttons, etc.
 *
 * NOTE: this intentionally returns true even when only *some* lamps are assigned
 * (e.g. mid-editing in a market-matched version). For the capability question
 * "can we run frontend measurement right now?" use `isFullyCatalogueAssigned`.
 */
export function isMarketMatchedVersion(version: Version | null | undefined): boolean {
  return !!version?.lights?.some((light) => light.variantId != null);
}

/**
 * True when EVERY luminaire in the version has a catalogue product/variant assigned.
 * This is the prerequisite for frontend measurement: the photometry of every lamp must
 * be known from a catalogue LDC file before we can compute illuminance.
 *
 * Capability rule (measurement + real-time render enabled) — use this together with
 * `isCalculationCompleted` from jobStatus.ts:
 *
 *   const canMeasureAndRender =
 *     isFullyCatalogueAssigned(version)   // pure-catalogue path (frontend engine)
 *     || isCalculationCompleted(version); // optimized or future hybrid path (ML engine)
 *
 * The separation keeps hybrid workflow support straightforward: a partially-assigned
 * version that has also been optimized satisfies the second branch automatically.
 */
export function isFullyCatalogueAssigned(version: Version | null | undefined): boolean {
  const lights = version?.lights;
  if (!lights || lights.length === 0) return false;
  return lights.every((light) => light.variantId != null);
}

type LuminaireMarketMatchInfo = {
  productId: number;
  productName: string;
  manufacturerName?: string;
  variantId: number;
  variantName: string;
  seriesId?: string;
  manufacturerId?: string;
};

/**
 * Resolves the applied market-match product + variant for a workspace luminaire,
 * using the active similarity candidate placements and loaded catalogue details.
 *
 * @param luminaireUuid - Persisted `Version.lights[].uuid` (`Lamp.uuid` in workspace state).
 */
export function getAppliedMarketMatchForLuminaire(
  luminaireUuid: string,
  activeVersion: Version | undefined,
  appliedSelection: { lampId: number; measurementFileId?: number } | null,
  lampSimilarityDetails: LampSimilarityDetail[],
): LuminaireMarketMatchInfo | null {
  if (!appliedSelection || !activeVersion?.lampSimilarityResult?.candidates?.length) {
    return null;
  }

  const candidate = activeVersion.lampSimilarityResult.candidates.find(
    (c) => c.lampId === appliedSelection.lampId && c.measurementFile?.id === appliedSelection.measurementFileId,
  );
  if (!candidate?.placements?.length) {
    return null;
  }

  const placement = candidate.placements.find((p) => p.tamanLuminaireUuid === luminaireUuid);
  if (!placement) {
    return null;
  }

  const detail = lampSimilarityDetails.find(
    (d) => d.lampId === candidate.lampId && d.measurementFileId === appliedSelection.measurementFileId,
  );

  const variant = detail?.variants?.find(
    (v) => v.id === placement.variantId || String(v.id) === String(placement.variantId),
  );

  return {
    productId: candidate.lampId,
    productName: detail?.name ?? `Product #${candidate.lampId}`,
    manufacturerName: detail?.manufacturerName,
    variantId: placement.variantId,
    variantName: variant?.name ?? `#${placement.variantId}`,
    seriesId: detail?.seriesId,
    manufacturerId: detail?.manufacturerId,
  };
}
